import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiAlertTriangle, FiInfo, FiRefreshCw } from 'react-icons/fi';
import { BarChartHoverLabel } from '@/components/admin/BarChartHoverLabel';
import { apiJson } from '@/lib/api';
import type {
  AdminActiveUsersMetricsResponse,
  AdminActivityMetricsResponse,
  AdminDailyCountSeries,
  AdminMetricsRange,
  AdminSubscribersMetricsResponse,
} from '@/lib/types';

const RANGE_OPTIONS: { range: AdminMetricsRange; label: string; description: string }[] = [
  { range: '24h', label: '24h', description: 'Today (Pacific)' },
  { range: '7d', label: '7d', description: 'Last 7 days' },
  { range: '30d', label: '30d', description: 'Last 30 days' },
  { range: '60d', label: '60d', description: 'Last 60 days' },
  { range: '90d', label: '90d', description: 'Last 90 days' },
];

const ALLOWED_RANGES = new Set<string>(RANGE_OPTIONS.map((r) => r.range));

type DailyPoint = { date: string; value: number };

type DailyRatioPoint = DailyPoint & {
  active_users: number;
  subscribers: number;
};

function formatRatioValue(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function niceDecimalAxisMax(value: number): number {
  if (value <= 0) return 1;
  const padded = value * 1.08;
  if (padded <= 0.5) return 0.5;
  if (padded <= 1) return 1;
  if (padded <= 2.5) return 2.5;
  if (padded <= 5) return 5;
  return niceAxisMax(padded);
}

function decimalAxisTicks(max: number): number[] {
  const step =
    max <= 0.5 ? 0.1 : max <= 1 ? 0.2 : max <= 2.5 ? 0.5 : max <= 5 ? 1 : Math.ceil(max / 4);
  const ticks: number[] = [];
  for (let v = 0; v <= max + step / 2; v += step) {
    ticks.push(Math.round(v * 100) / 100);
  }
  if (ticks[ticks.length - 1] < max) ticks.push(max);
  return ticks;
}

function buildActiveUsersPerSubscriberDaily(
  activeData: AdminActiveUsersMetricsResponse,
  subscribersData: AdminSubscribersMetricsResponse,
): DailyRatioPoint[] {
  const subscribersByDate = new Map(
    subscribersData.daily.map((d) => [d.date, d.subscribers]),
  );

  return activeData.daily.map((d) => {
    const subscribers = subscribersByDate.get(d.date) ?? 0;
    const value =
      subscribers > 0
        ? Math.round((d.active_users / subscribers) * 100) / 100
        : 0;

    return {
      date: d.date,
      value,
      active_users: d.active_users,
      subscribers,
    };
  });
}

function formatDateLabel(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatAxisDate(ymd: string, showYear: boolean): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    ...(showYear ? { year: '2-digit' } : {}),
  });
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function niceAxisMax(value: number): number {
  if (value <= 0) return 5;
  if (value <= 5) return 5;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function yAxisTicks(max: number): number[] {
  const top = niceAxisMax(max);
  const step =
    top <= 5 ? 1 : top <= 10 ? 2 : top <= 25 ? 5 : top <= 50 ? 10 : Math.ceil(top / 4 / 10) * 10;
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] !== top) ticks.push(top);
  return ticks;
}

function xLabelIndices(count: number): number[] {
  if (count <= 1) return [0];
  if (count <= 8) return Array.from({ length: count }, (_, i) => i);
  const targetLabels = count <= 31 ? 7 : 10;
  const step = Math.max(1, Math.round((count - 1) / (targetLabels - 1)));
  const indices: number[] = [0];
  for (let i = step; i < count - 1; i += step) indices.push(i);
  if (indices[indices.length - 1] !== count - 1) indices.push(count - 1);
  return indices;
}

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
        {label}
      </p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
        {value}
      </p>
      {hint ? <p className='mt-1 text-xs text-gray-500'>{hint}</p> : null}
    </div>
  );
}

function ActivityMetricSection({
  range,
  title,
  subtitle,
  yAxisLabel,
  valueLabel,
  barColor,
  series,
}: {
  range: AdminMetricsRange;
  title: string;
  subtitle: string;
  yAxisLabel: string;
  valueLabel: string;
  barColor: string;
  series: AdminDailyCountSeries;
}) {
  const latestDay = series.daily[series.daily.length - 1];

  return (
    <>
      <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
        <StatTile
          label={range === '24h' ? 'Today' : 'Latest day'}
          value={series.latest.toLocaleString()}
          hint={latestDay ? formatDateLabel(latestDay.date) : undefined}
        />
        <StatTile
          label='Daily average'
          value={series.average.toLocaleString(undefined, {
            maximumFractionDigits: 1,
          })}
        />
        <StatTile label='Peak day' value={series.peak.toLocaleString()} />
      </div>

      <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
        <DailyMetricBarChart
          title={title}
          subtitle={subtitle}
          yAxisLabel={yAxisLabel}
          valueLabel={valueLabel}
          barColor={barColor}
          daily={series.daily.map((d) => ({
            date: d.date,
            value: d.count,
          }))}
        />
      </section>
    </>
  );
}

function DailyMetricBarChart({
  title,
  subtitle,
  yAxisLabel,
  valueLabel,
  barColor,
  daily,
  valueFormat = 'integer',
  tooltipDetail,
}: {
  title: string;
  subtitle: string;
  yAxisLabel: string;
  valueLabel: string;
  barColor: string;
  daily: DailyPoint[];
  valueFormat?: 'integer' | 'decimal';
  tooltipDetail?: (point: DailyPoint) => string | null;
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    const updateWidth = () => {
      const next = el.clientWidth;
      if (next > 0) setChartWidth(next);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (!daily.length || chartWidth <= 0) return null;

    const n = daily.length;
    const barGap = n <= 31 ? 2 : 1;
    const padLeft = 48;
    const padRight = 12;
    const padTop = 20;
    const padBottom = 52;
    const width = chartWidth;
    const height = 280;
    const innerW = width - padLeft - padRight;
    const innerH = height - padTop - padBottom;
    const slotW = innerW / n;
    const barW = Math.max(2, slotW - barGap);
    const maxValue = Math.max(...daily.map((d) => d.value));
    const axisMax =
      valueFormat === 'decimal'
        ? niceDecimalAxisMax(maxValue)
        : niceAxisMax(maxValue);
    const ticks =
      valueFormat === 'decimal'
        ? decimalAxisTicks(axisMax)
        : yAxisTicks(maxValue);
    const labelIndices = new Set(xLabelIndices(n));
    const showBarValues = n <= 14 || barW >= 18;
    const firstYear = daily[0].date.slice(0, 4);
    const lastYear = daily[n - 1].date.slice(0, 4);
    const showYearOnAxis = firstYear !== lastYear;

    return {
      n,
      barGap,
      barW,
      slotW,
      width,
      height,
      padLeft,
      padTop,
      padBottom,
      innerW,
      innerH,
      axisMax,
      ticks,
      labelIndices,
      showBarValues,
      showYearOnAxis,
    };
  }, [daily, chartWidth, valueFormat]);

  const formatValue =
    valueFormat === 'decimal'
      ? formatRatioValue
      : (value: number) => value.toLocaleString();

  if (!daily.length) {
    return (
      <p className='text-sm text-gray-500'>No daily data in this date range.</p>
    );
  }

  const hoveredPoint =
    hoveredIndex !== null && layout ? daily[hoveredIndex] : null;
  const hoveredBar =
    hoveredIndex !== null && layout
      ? (() => {
          const h =
            layout.axisMax > 0
              ? (daily[hoveredIndex].value / layout.axisMax) * layout.innerH
              : 0;
          const x =
            layout.padLeft +
            hoveredIndex * layout.slotW +
            layout.barGap / 2;
          const y = layout.height - layout.padBottom - h;
          const cx = x + layout.barW / 2;
          return { cx, y, h };
        })()
      : null;

  if (!layout) {
    return (
      <div className='min-w-0'>
        <div className='mb-3 flex flex-wrap items-baseline justify-between gap-2'>
          <div>
            <p className='text-sm font-semibold text-gray-900'>{title}</p>
            <p className='text-xs text-gray-500'>{subtitle}</p>
          </div>
        </div>
        <div
          ref={chartRef}
          className='h-[280px] rounded-md border border-gray-100 bg-gray-50/50'
        />
      </div>
    );
  }

  const {
    n,
    barGap,
    barW,
    slotW,
    width,
    height,
    padLeft,
    padTop,
    padBottom,
    innerW,
    innerH,
    axisMax,
    ticks,
    labelIndices,
    showBarValues,
    showYearOnAxis,
  } = layout;

  const baselineY = height - padBottom;

  return (
    <div className='min-w-0'>
      <div className='mb-3 flex flex-wrap items-baseline justify-between gap-2'>
        <div>
          <p className='text-sm font-semibold text-gray-900'>{title}</p>
          <p className='text-xs text-gray-500'>{subtitle}</p>
        </div>
        <p className='text-xs text-gray-500 tabular-nums'>
          {formatDateLabel(daily[0].date)} – {formatDateLabel(daily[n - 1].date)}
        </p>
      </div>

      <div
        ref={chartRef}
        className='rounded-md border border-gray-100 bg-gray-50/50 pb-1'
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width='100%'
          height={height}
          role='img'
          aria-label={`${title} bar chart`}
        >
          <text
            x={14}
            y={padTop + innerH / 2}
            transform={`rotate(-90 14 ${padTop + innerH / 2})`}
            textAnchor='middle'
            className='fill-gray-500 text-[11px] font-medium'
          >
            {yAxisLabel}
          </text>

          {ticks.map((tick) => {
            const y = baselineY - (tick / axisMax) * innerH;
            return (
              <g key={tick}>
                <line
                  x1={padLeft}
                  y1={y}
                  x2={padLeft + innerW}
                  y2={y}
                  stroke={tick === 0 ? '#d1d5db' : '#e5e7eb'}
                  strokeDasharray={tick === 0 ? undefined : '4 4'}
                />
                <text
                  x={padLeft - 8}
                  y={y + 4}
                  textAnchor='end'
                  className='fill-gray-500 text-[10px] tabular-nums'
                >
                  {formatValue(tick)}
                </text>
              </g>
            );
          })}

          {daily.map((d, i) => {
            const h = axisMax > 0 ? (d.value / axisMax) * innerH : 0;
            const x = padLeft + i * slotW + barGap / 2;
            const y = baselineY - h;
            const cx = x + barW / 2;
            const showXLabel = labelIndices.has(i);
            const isHovered = hoveredIndex === i;

            return (
              <g key={d.date}>
                <rect
                  x={padLeft + i * slotW}
                  y={padTop}
                  width={slotW}
                  height={innerH}
                  fill='transparent'
                  className='cursor-pointer'
                  onMouseEnter={() => setHoveredIndex(i)}
                />

                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={Math.max(0, h)}
                  fill={barColor}
                  fillOpacity={isHovered ? 1 : hoveredIndex !== null ? 0.55 : 0.85}
                  rx={2}
                  className='pointer-events-none'
                />

                {showBarValues && d.value > 0 && hoveredIndex === null ? (
                  <text
                    x={cx}
                    y={Math.max(padTop + 10, y - 4)}
                    textAnchor='middle'
                    className='fill-gray-700 text-[10px] font-semibold tabular-nums pointer-events-none'
                  >
                    {formatValue(d.value)}
                  </text>
                ) : null}

                {showXLabel ? (
                  <text
                    x={cx}
                    y={baselineY + 14}
                    textAnchor='middle'
                    className='fill-gray-600 text-[10px] pointer-events-none'
                  >
                    {formatAxisDate(d.date, showYearOnAxis)}
                  </text>
                ) : null}
              </g>
            );
          })}

          {hoveredPoint && hoveredBar ? (
            <BarChartHoverLabel
              x={hoveredBar.cx}
              barTopY={hoveredBar.y}
              minX={padLeft + 30}
              maxX={padLeft + innerW - 30}
              minY={padTop + 4}
              text={[
                formatDateLabel(hoveredPoint.date),
                `${formatValue(hoveredPoint.value)} ${valueLabel}`,
                tooltipDetail?.(hoveredPoint) ?? null,
              ]
                .filter(Boolean)
                .join(' · ')}
            />
          ) : null}

          <text
            x={padLeft + innerW / 2}
            y={height - 8}
            textAnchor='middle'
            className='fill-gray-500 text-[11px] font-medium'
          >
            Date (Pacific)
          </text>
        </svg>
      </div>
    </div>
  );
}

function DailyMetricLineChart({
  title,
  subtitle,
  yAxisLabel,
  valueLabel,
  lineColor,
  daily,
  formatValue = formatRatioValue,
  tooltipDetail,
}: {
  title: string;
  subtitle: string;
  yAxisLabel: string;
  valueLabel: string;
  lineColor: string;
  daily: DailyPoint[];
  formatValue?: (value: number) => string;
  tooltipDetail?: (point: DailyPoint) => string | null;
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    const updateWidth = () => {
      const next = el.clientWidth;
      if (next > 0) setChartWidth(next);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (!daily.length || chartWidth <= 0) return null;

    const n = daily.length;
    const padLeft = 48;
    const padRight = 12;
    const padTop = 20;
    const padBottom = 52;
    const width = chartWidth;
    const height = 280;
    const innerW = width - padLeft - padRight;
    const innerH = height - padTop - padBottom;
    const slotW = innerW / n;
    const maxValue = Math.max(...daily.map((d) => d.value));
    const axisMax = niceDecimalAxisMax(maxValue);
    const ticks = decimalAxisTicks(axisMax);
    const labelIndices = new Set(xLabelIndices(n));
    const firstYear = daily[0].date.slice(0, 4);
    const lastYear = daily[n - 1].date.slice(0, 4);
    const showYearOnAxis = firstYear !== lastYear;

    const points = daily.map((d, i) => {
      const cx = padLeft + i * slotW + slotW / 2;
      const cy =
        height -
        padBottom -
        (axisMax > 0 ? (d.value / axisMax) * innerH : 0);
      return { ...d, cx, cy, index: i };
    });

    const linePath = points
      .map((point, i) => `${i === 0 ? 'M' : 'L'} ${point.cx} ${point.cy}`)
      .join(' ');

    return {
      n,
      slotW,
      width,
      height,
      padLeft,
      padTop,
      padBottom,
      innerW,
      innerH,
      axisMax,
      ticks,
      labelIndices,
      showYearOnAxis,
      points,
      linePath,
    };
  }, [daily, chartWidth]);

  if (!daily.length) {
    return (
      <p className='text-sm text-gray-500'>No daily data in this date range.</p>
    );
  }

  const hoveredPoint =
    hoveredIndex !== null && layout ? layout.points[hoveredIndex] : null;

  if (!layout) {
    return (
      <div className='min-w-0'>
        <div className='mb-3 flex flex-wrap items-baseline justify-between gap-2'>
          <div>
            <p className='text-sm font-semibold text-gray-900'>{title}</p>
            <p className='text-xs text-gray-500'>{subtitle}</p>
          </div>
        </div>
        <div
          ref={chartRef}
          className='h-[280px] rounded-md border border-gray-100 bg-gray-50/50'
        />
      </div>
    );
  }

  const {
    n,
    slotW,
    width,
    height,
    padLeft,
    padTop,
    padBottom,
    innerW,
    innerH,
    axisMax,
    ticks,
    labelIndices,
    showYearOnAxis,
    points,
    linePath,
  } = layout;

  const baselineY = height - padBottom;

  return (
    <div className='min-w-0'>
      <div className='mb-3 flex flex-wrap items-baseline justify-between gap-2'>
        <div>
          <p className='text-sm font-semibold text-gray-900'>{title}</p>
          <p className='text-xs text-gray-500'>{subtitle}</p>
        </div>
        <p className='text-xs text-gray-500 tabular-nums'>
          {formatDateLabel(daily[0].date)} – {formatDateLabel(daily[n - 1].date)}
        </p>
      </div>

      <div
        ref={chartRef}
        className='rounded-md border border-gray-100 bg-gray-50/50 pb-1'
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width='100%'
          height={height}
          role='img'
          aria-label={`${title} line chart`}
        >
          <text
            x={14}
            y={padTop + innerH / 2}
            transform={`rotate(-90 14 ${padTop + innerH / 2})`}
            textAnchor='middle'
            className='fill-gray-500 text-[11px] font-medium'
          >
            {yAxisLabel}
          </text>

          {ticks.map((tick) => {
            const y = baselineY - (tick / axisMax) * innerH;
            return (
              <g key={tick}>
                <line
                  x1={padLeft}
                  y1={y}
                  x2={padLeft + innerW}
                  y2={y}
                  stroke={tick === 0 ? '#d1d5db' : '#e5e7eb'}
                  strokeDasharray={tick === 0 ? undefined : '4 4'}
                />
                <text
                  x={padLeft - 8}
                  y={y + 4}
                  textAnchor='end'
                  className='fill-gray-500 text-[10px] tabular-nums'
                >
                  {formatValue(tick)}
                </text>
              </g>
            );
          })}

          {n > 1 ? (
            <path
              d={linePath}
              fill='none'
              stroke={lineColor}
              strokeWidth={2.5}
              strokeLinejoin='round'
              strokeLinecap='round'
              className='pointer-events-none'
            />
          ) : null}

          {points.map((point) => {
            const showXLabel = labelIndices.has(point.index);
            const isHovered = hoveredIndex === point.index;

            return (
              <g key={point.date}>
                <rect
                  x={padLeft + point.index * slotW}
                  y={padTop}
                  width={slotW}
                  height={innerH}
                  fill='transparent'
                  className='cursor-pointer'
                  onMouseEnter={() => setHoveredIndex(point.index)}
                />

                <circle
                  cx={point.cx}
                  cy={point.cy}
                  r={isHovered ? 5 : 3.5}
                  fill={isHovered ? lineColor : '#ffffff'}
                  stroke={lineColor}
                  strokeWidth={isHovered ? 0 : 2}
                  className='pointer-events-none'
                  opacity={hoveredIndex === null || isHovered ? 1 : 0.45}
                />

                {showXLabel ? (
                  <text
                    x={point.cx}
                    y={baselineY + 14}
                    textAnchor='middle'
                    className='fill-gray-600 text-[10px] pointer-events-none'
                  >
                    {formatAxisDate(point.date, showYearOnAxis)}
                  </text>
                ) : null}
              </g>
            );
          })}

          {hoveredPoint ? (
            <g pointerEvents='none'>
              <line
                x1={hoveredPoint.cx}
                y1={padTop}
                x2={hoveredPoint.cx}
                y2={baselineY}
                stroke={lineColor}
                strokeOpacity={0.25}
                strokeWidth={1}
              />
              {(() => {
                const detail = tooltipDetail?.(hoveredPoint);
                const label = `${formatValue(hoveredPoint.value)} ${valueLabel}`;
                const dateLabel = formatDateLabel(hoveredPoint.date);
                const tooltipW = Math.max(
                  108,
                  Math.max(label.length, detail?.length ?? 0) * 6.2 + 20,
                );
                const tooltipH = detail ? 54 : 40;
                const tooltipX = Math.min(
                  Math.max(hoveredPoint.cx - tooltipW / 2, padLeft),
                  padLeft + innerW - tooltipW,
                );
                const aboveY = hoveredPoint.cy - tooltipH - 12;
                const tooltipY =
                  aboveY >= padTop ? aboveY : hoveredPoint.cy + 12;

                return (
                  <>
                    <rect
                      x={tooltipX}
                      y={tooltipY}
                      width={tooltipW}
                      height={tooltipH}
                      rx={6}
                      fill='#111827'
                      fillOpacity={0.92}
                    />
                    <text
                      x={tooltipX + tooltipW / 2}
                      y={tooltipY + 15}
                      textAnchor='middle'
                      className='fill-white text-[10px] font-medium'
                    >
                      {dateLabel}
                    </text>
                    <text
                      x={tooltipX + tooltipW / 2}
                      y={tooltipY + 30}
                      textAnchor='middle'
                      className='fill-white text-[11px] font-semibold tabular-nums'
                    >
                      {label}
                    </text>
                    {detail ? (
                      <text
                        x={tooltipX + tooltipW / 2}
                        y={tooltipY + 44}
                        textAnchor='middle'
                        className='fill-white/80 text-[10px] tabular-nums'
                      >
                        {detail}
                      </text>
                    ) : null}
                  </>
                );
              })()}
            </g>
          ) : null}

          <text
            x={padLeft + innerW / 2}
            y={height - 8}
            textAnchor='middle'
            className='fill-gray-500 text-[11px] font-medium'
          >
            Date (Pacific)
          </text>
        </svg>
      </div>

      <p className='mt-2 text-[11px] text-gray-400'>
        Hover a point for the exact ratio.
      </p>
    </div>
  );
}

function SubscribersSourceBanner({ data }: { data: AdminSubscribersMetricsResponse }) {
  if (!data.ok) {
    return (
      <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-2'>
        <FiAlertTriangle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden />
        <div className='min-w-0 space-y-1'>
          <p className='font-semibold'>Subscriber chart unavailable</p>
          <p className='text-xs'>{data.error ?? 'RevenueCat Charts API is not configured.'}</p>
          {!data.env_configured ? (
            <p className='text-xs text-amber-900/80'>
              Set <span className='font-mono'>REVENUECAT_CHARTS_API_KEY</span> (v2 secret with
              charts read) and <span className='font-mono'>REVENUECAT_PROJECT_ID</span> on the API
              deployment. Legacy v1 subscriber keys do not work for Charts.
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  if (data.cache_status === 'stale') {
    return (
      <div className='rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-2.5 text-xs text-amber-950 flex gap-2'>
        <FiAlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' aria-hidden />
        <p>
          <span className='font-semibold'>Cached subscriber data</span>
          {data.error ? ` — ${data.error}` : ''}
          {data.cached_at ? ` · cached ${formatDateTime(data.cached_at)}` : ''}
        </p>
      </div>
    );
  }

  return (
    <p className='text-[11px] text-gray-400'>
      RevenueCat {data.chart_display_name ?? 'actives'} chart
      {data.revenuecat_last_computed_at
        ? ` · RC computed ${formatDateTime(data.revenuecat_last_computed_at)}`
        : ''}
      {data.cached_at && data.cache_status !== 'bypass'
        ? ` · cached ${formatDateTime(data.cached_at)} (${data.cache_status})`
        : data.cache_status === 'bypass'
          ? ' · live fetch'
          : ''}
    </p>
  );
}

function AdminMetricsPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { range: rangeParam } = Route.useSearch();

  const range: AdminMetricsRange = ALLOWED_RANGES.has(rangeParam ?? '')
    ? (rangeParam as AdminMetricsRange)
    : '30d';

  const activeRangeOption =
    RANGE_OPTIONS.find((opt) => opt.range === range) ?? RANGE_OPTIONS[2];

  const activeUsersQuery = useQuery({
    queryKey: ['admin', 'metrics', 'active-users', range],
    queryFn: () =>
      apiJson<AdminActiveUsersMetricsResponse>(
        getToken,
        `/v1/admin/metrics/active-users?range=${encodeURIComponent(range)}`,
      ),
  });

  const subscribersQuery = useQuery({
    queryKey: ['admin', 'metrics', 'subscribers', range],
    queryFn: () =>
      apiJson<AdminSubscribersMetricsResponse>(
        getToken,
        `/v1/admin/metrics/subscribers?range=${encodeURIComponent(range)}`,
      ),
  });

  const activityQuery = useQuery({
    queryKey: ['admin', 'metrics', 'activity', range],
    queryFn: () =>
      apiJson<AdminActivityMetricsResponse>(
        getToken,
        `/v1/admin/metrics/activity?range=${encodeURIComponent(range)}`,
      ),
  });

  const activeData = activeUsersQuery.data;
  const subscribersData = subscribersQuery.data;
  const activityData = activityQuery.data;
  const latestActiveDay = activeData?.daily[activeData.daily.length - 1];
  const isLoading =
    activeUsersQuery.isLoading ||
    subscribersQuery.isLoading ||
    activityQuery.isLoading;
  const isFetching =
    activeUsersQuery.isFetching ||
    subscribersQuery.isFetching ||
    activityQuery.isFetching;

  const ratioDaily = useMemo(() => {
    if (!activeData || !subscribersData?.ok) return [];
    return buildActiveUsersPerSubscriberDaily(activeData, subscribersData);
  }, [activeData, subscribersData]);

  const ratioStats = useMemo(() => {
    const validDays = ratioDaily.filter((d) => d.subscribers > 0);
    if (!validDays.length) {
      return { latest: 0, average: 0, peak: 0, peakDate: null as string | null };
    }

    const values = validDays.map((d) => d.value);
    const latest = validDays[validDays.length - 1]?.value ?? 0;
    const average = values.reduce((sum, v) => sum + v, 0) / values.length;
    const peak = Math.max(...values);
    const peakDate = validDays.find((d) => d.value === peak)?.date ?? null;

    return { latest, average, peak, peakDate };
  }, [ratioDaily]);

  const setRange = (next: AdminMetricsRange) => {
    navigate({ to: '/admin/metrics', search: { range: next } });
  };

  const handleRefresh = () => {
    void activeUsersQuery.refetch();
    void activityQuery.refetch();
    void queryClient.fetchQuery({
      queryKey: ['admin', 'metrics', 'subscribers', range],
      queryFn: () =>
        apiJson<AdminSubscribersMetricsResponse>(
          getToken,
          `/v1/admin/metrics/subscribers?range=${encodeURIComponent(range)}&refresh=true`,
        ),
    });
  };

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
        <div className='min-w-0'>
          <h1 className='text-xl font-semibold text-gray-900 sm:text-2xl'>Metrics</h1>
          <p className='mt-1 text-sm text-gray-500'>
            Product usage and subscription trends. Active users match Pulse; subscribers come from
            RevenueCat active paying subscriptions.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <div
            className='inline-flex flex-col gap-1.5'
            role='group'
            aria-label='Time range'
          >
            <span className='text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-0.5'>
              Time range
            </span>
            <div className='inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm'>
              {RANGE_OPTIONS.map((opt) => {
                const active = range === opt.range;
                return (
                  <button
                    key={opt.range}
                    type='button'
                    aria-pressed={active}
                    title={opt.description}
                    onClick={() => setRange(opt.range)}
                    className={`min-w-[3rem] rounded-md px-3 py-2 text-xs font-bold transition-all ${
                      active
                        ? 'bg-primary text-white shadow-sm ring-2 ring-primary/30'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type='button'
            onClick={handleRefresh}
            disabled={isFetching}
            className='inline-flex items-center gap-1.5 self-end rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60'
          >
            <FiRefreshCw
              className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        </div>
      ) : (
        <>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500'>
            <span className='inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary'>
              {activeRangeOption.label} · {activeRangeOption.description}
            </span>
            {activeData ? (
              <span className='inline-flex items-center gap-1.5'>
                <FiInfo className='h-3.5 w-3.5 shrink-0' aria-hidden />
                {activeData.start_date} → {activeData.end_date} ({activeData.timezone})
              </span>
            ) : null}
          </div>

          {activeUsersQuery.isError || !activeData ? (
            <p className='text-red-600'>Could not load active user metrics.</p>
          ) : (
            <>
              <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                <StatTile
                  label={range === '24h' ? 'Active today' : 'Latest day'}
                  value={(latestActiveDay?.active_users ?? 0).toLocaleString()}
                  hint={
                    latestActiveDay ? formatDateLabel(latestActiveDay.date) : undefined
                  }
                />
                <StatTile
                  label='Daily average'
                  value={activeData.average_active_users.toLocaleString(undefined, {
                    maximumFractionDigits: 1,
                  })}
                />
                <StatTile
                  label='Peak day'
                  value={activeData.peak_active_users.toLocaleString()}
                />
              </div>

              <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
                <DailyMetricBarChart
                  title='Active users per day'
                  subtitle='Distinct users with ≥1 audit event · US Pacific days'
                  yAxisLabel='Active users'
                  valueLabel='active users'
                  barColor='#018A9D'
                  daily={activeData.daily.map((d) => ({
                    date: d.date,
                    value: d.active_users,
                  }))}
                />
              </section>
            </>
          )}

          {activityQuery.isError || !activityData ? (
            <p className='text-red-600'>Could not load product activity metrics.</p>
          ) : (
            <div className='space-y-6'>
              <div>
                <h2 className='text-lg font-semibold text-gray-900'>Product activity</h2>
                <p className='mt-1 text-sm text-gray-500'>
                  Daily creations by Pacific calendar day. Messages are main household chat only.
                </p>
              </div>

              <ActivityMetricSection
                range={range}
                title='New users per day'
                subtitle='User accounts created · excludes internal test accounts'
                yAxisLabel='New users'
                valueLabel='new users'
                barColor='#10b981'
                series={activityData.new_users}
              />

              <ActivityMetricSection
                range={range}
                title='Messages per day'
                subtitle='Main household chat messages created'
                yAxisLabel='Messages'
                valueLabel='messages'
                barColor='#3b82f6'
                series={activityData.messages}
              />

              <ActivityMetricSection
                range={range}
                title='Expenses created per day'
                subtitle='Non-deleted expenses created'
                yAxisLabel='Expenses'
                valueLabel='expenses'
                barColor='#ec4899'
                series={activityData.expenses}
              />

              <ActivityMetricSection
                range={range}
                title='Journal entries per day'
                subtitle='Non-deleted journal entries created'
                yAxisLabel='Entries'
                valueLabel='journal entries'
                barColor='#8b5cf6'
                series={activityData.journal_entries}
              />
            </div>
          )}

          {subscribersQuery.isError || !subscribersData ? (
            <p className='text-red-600'>Could not load subscriber metrics.</p>
          ) : (
            <>
              <SubscribersSourceBanner data={subscribersData} />

              {subscribersData.ok ? (
                <>
                  <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                    <StatTile
                      label={range === '24h' ? 'Subscribers today' : 'Latest day'}
                      value={subscribersData.latest_subscribers.toLocaleString()}
                      hint={
                        subscribersData.daily.length
                          ? formatDateLabel(
                              subscribersData.daily[subscribersData.daily.length - 1].date,
                            )
                          : undefined
                      }
                    />
                    <StatTile
                      label='Daily average'
                      value={subscribersData.average_subscribers.toLocaleString(
                        undefined,
                        { maximumFractionDigits: 1 },
                      )}
                    />
                    <StatTile
                      label='Peak day'
                      value={subscribersData.peak_subscribers.toLocaleString()}
                    />
                  </div>

                  <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
                    <DailyMetricBarChart
                      title='Active subscribers per day'
                      subtitle='RevenueCat active paying subscriptions (end-of-day snapshot)'
                      yAxisLabel='Subscribers'
                      valueLabel='subscribers'
                      barColor='#6366f1'
                      daily={subscribersData.daily.map((d) => ({
                        date: d.date,
                        value: d.subscribers,
                      }))}
                    />
                  </section>

                  {activeData ? (
                    <>
                      <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                        <StatTile
                          label={range === '24h' ? 'Ratio today' : 'Latest day'}
                          value={formatRatioValue(ratioStats.latest)}
                          hint={
                            ratioDaily.length
                              ? formatDateLabel(ratioDaily[ratioDaily.length - 1].date)
                              : undefined
                          }
                        />
                        <StatTile
                          label='Daily average'
                          value={formatRatioValue(ratioStats.average)}
                        />
                        <StatTile
                          label='Peak day'
                          value={formatRatioValue(ratioStats.peak)}
                          hint={
                            ratioStats.peakDate
                              ? formatDateLabel(ratioStats.peakDate)
                              : undefined
                          }
                        />
                      </div>

                      <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
                        <DailyMetricLineChart
                          title='Active users / active subscribers'
                          subtitle='Daily active users ÷ paying subscribers · derived from the charts above'
                          yAxisLabel='Ratio'
                          valueLabel='users per subscriber'
                          lineColor='#f59e0b'
                          daily={ratioDaily}
                          tooltipDetail={(point) => {
                            const ratioPoint = point as DailyRatioPoint;
                            return `${ratioPoint.active_users.toLocaleString()} active ÷ ${ratioPoint.subscribers.toLocaleString()} subscribers`;
                          }}
                        />
                      </section>
                    </>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/metrics')({
  validateSearch: (search: Record<string, unknown>) => ({
    range:
      typeof search.range === 'string' && ALLOWED_RANGES.has(search.range)
        ? search.range
        : '30d',
  }),
  component: AdminMetricsPage,
});
